import { Injectable, inject } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { ReferenceDataService } from '@core/services/reference-data.service';
import { ReferenceDataActions } from './reference-data.actions';

/**
 * ReferenceDataState tracks the *status* of the local reference-data
 * cache, not its contents. The actual records live in IndexedDB (via
 * Dexie) because (a) there's a lot of them and we don't need them all
 * in memory on every page, and (b) IndexedDB gives us free persistence
 * across reloads without hitting the NGXS storage-plugin quota.
 *
 * Pages that need reference rows pull them directly from
 * ReferenceDataService methods, awaiting the promise. This state slice
 * exists so the app shell can show a one-time loading indicator while
 * seeding completes and so other features can subscribe to "are we
 * ready" before issuing queries.
 */
export interface ReferenceDataStateModel {
  /**
   * Lifecycle status of the reference-data cache:
   *   - 'uninitialized': Dexie has not been populated yet
   *   - 'seeding':       currently loading from bundled seed-data.json
   *   - 'ready':         cache is usable (seeded or synced)
   *   - 'syncing':       a background sync is in progress
   *   - 'failed':        seed load failed; UI should surface this
   */
  status: 'uninitialized' | 'seeding' | 'ready' | 'syncing' | 'failed';
  lastSyncedAt: string | null;
  lastError: string | null;
}

export const INITIAL_REFERENCE_DATA_STATE: ReferenceDataStateModel = {
  status: 'uninitialized',
  lastSyncedAt: null,
  lastError: null,
};

@State<ReferenceDataStateModel>({
  name: 'referenceData',
  defaults: INITIAL_REFERENCE_DATA_STATE,
})
@Injectable()
export class ReferenceDataState {
  private readonly referenceDataService = inject(ReferenceDataService);

  @Selector()
  static whole(state: ReferenceDataStateModel): ReferenceDataStateModel {
    return state;
  }

  @Selector()
  static status(state: ReferenceDataStateModel): ReferenceDataStateModel['status'] {
    return state.status;
  }

  @Selector()
  static isReady(state: ReferenceDataStateModel): boolean {
    return state.status === 'ready' || state.status === 'syncing';
  }

  @Action(ReferenceDataActions.EnsureInitialized)
  async ensureInitialized(ctx: StateContext<ReferenceDataStateModel>) {
    if (ctx.getState().status === 'ready' || ctx.getState().status === 'syncing') {
      return;
    }
    ctx.patchState({ status: 'seeding', lastError: null });

    try {
      await this.referenceDataService.ensureInitialized();
      ctx.patchState({ status: 'ready' });
      ctx.dispatch(new ReferenceDataActions.SeedLoaded());
    } catch (err) {
      ctx.patchState({
        status: 'failed',
        lastError: err instanceof Error ? err.message : String(err),
      });
    }
  }

  @Action(ReferenceDataActions.SyncIfStale)
  async syncIfStale(ctx: StateContext<ReferenceDataStateModel>) {
    const current = ctx.getState();
    if (current.status !== 'ready') {
      // Must be seeded first; caller shouldn't trigger this otherwise.
      return;
    }
    ctx.patchState({ status: 'syncing', lastError: null });

    try {
      const refreshed = await this.referenceDataService.syncIfStale();
      ctx.patchState({
        status: 'ready',
        lastSyncedAt: refreshed ? new Date().toISOString() : current.lastSyncedAt,
      });
      ctx.dispatch(new ReferenceDataActions.SyncSuccess(refreshed));
    } catch (err) {
      ctx.patchState({
        status: 'ready', // cache is still usable even if sync failed
        lastError: err instanceof Error ? err.message : String(err),
      });
      ctx.dispatch(
        new ReferenceDataActions.SyncFailure(
          err instanceof Error ? err.message : String(err),
        ),
      );
    }
  }
}
